import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, useThemeStore } from '@/stores';
import { dashboardService } from '@/services';
import { apiClient } from '@/services/api-client';
import { playTicketChime, playUrgentTicketChime, showDesktopNotification } from '@/utils/ticket-notifications';
import { Ticket, X, ArrowLeft, Volume2, AlertCircle } from 'lucide-react';

interface ToastTicket {
  id: string;
  displayId: string;
  customerName: string;
  subject: string;
  priority: string;
  status: string;
  createdAt: string;
}

export const GlobalTicketNotifier: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const { theme, language } = useThemeStore();
  const isLight = theme === 'light';
  const isRTL = language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();

  const [activeToast, setActiveToast] = useState<{ id: string; tickets: ToastTicket[] } | null>(null);
  const knownTicketIdsRef = useRef<Set<string>>(new Set());
  const firstFetchDoneRef = useRef<boolean>(false);
  const userKeyRef = useRef<string>('');

  const currentUsername = (user?.username || (user as any)?.preferred_username || 'user').toLowerCase();

  // Reset or load known IDs when user changes
  useEffect(() => {
    if (!isAuthenticated || !currentUsername) return;

    userKeyRef.current = currentUsername;
    firstFetchDoneRef.current = false;
    const storageKey = 'ftth_known_tickets_' + currentUsername;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          knownTicketIdsRef.current = new Set(parsed);
        } else {
          knownTicketIdsRef.current = new Set();
        }
      } else {
        knownTicketIdsRef.current = new Set();
      }
    } catch {
      knownTicketIdsRef.current = new Set();
    }
  }, [isAuthenticated, currentUsername]);

  const checkForNewTickets = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const res = await dashboardService.supportTicketsList({
        pageSize: 20,
        pageNumber: 1,
        'sortCriteria.property': 'createdAt',
        'sortCriteria.direction': 'desc',
        hierarchyLevel: 0,
      }).catch(() => null);

      const items = res?.data?.items || res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
      if (!Array.isArray(items) || items.length === 0) return;

      const incomingTickets: ToastTicket[] = items.map((t: any) => {
        const rawStatus = t.status?.displayValue || t.status?.id || t.status || t.state?.displayValue || t.state?.id || t.state || '';
        return {
          id: String(t.self?.id || t.id || ''),
          displayId: String(t.displayId || t.self?.id || t.id || ''),
          customerName: t.customer?.displayValue || t.customerName || t.customer || 'مشترك',
          subject: t.subject || t.title || 'طلب دعم فني',
          priority: t.priority?.displayValue || t.priority || 'NORMAL',
          status: String(rawStatus),
          createdAt: t.createdAt || new Date().toISOString(),
        };
      }).filter((t: ToastTicket) => !!t.id);

      // On first fetch after loading the app: Seed all existing tickets into known set without firing alerts
      if (!firstFetchDoneRef.current) {
        incomingTickets.forEach((t) => knownTicketIdsRef.current.add(t.id));
        firstFetchDoneRef.current = true;
        try {
          const storageKey = 'ftth_known_tickets_' + (userKeyRef.current || 'user');
          localStorage.setItem(storageKey, JSON.stringify(Array.from(knownTicketIdsRef.current)));
        } catch {}
        return;
      }

      // Subsequent runs: detect genuinely new incoming tickets
      const newTickets = incomingTickets.filter((t) => !knownTicketIdsRef.current.has(t.id));

      if (newTickets.length > 0) {
        // Register new IDs into known set
        newTickets.forEach((t) => knownTicketIdsRef.current.add(t.id));
        try {
          const storageKey = 'ftth_known_tickets_' + (userKeyRef.current || 'user');
          const idsArray = Array.from(knownTicketIdsRef.current).slice(-100);
          localStorage.setItem(storageKey, JSON.stringify(idsArray));
        } catch {}

        // 1. Dispatch immediate background mobile push via Backend
        try {
          apiClient.post('/notifications/trigger-ticket-push', {
            tickets: newTickets.map((t) => ({
              id: t.id,
              displayId: t.displayId,
              customerName: t.customerName,
              subject: t.subject,
              priority: t.priority,
              status: t.status,
              createdAt: t.createdAt
            })),
            userId: userKeyRef.current || 'user'
          }).catch((pushErr) => {
            console.warn('[GlobalTicketNotifier] Mobile push trigger error:', pushErr);
          });
        } catch (dispatchErr) {
          console.warn('[GlobalTicketNotifier] Failed to trigger mobile push:', dispatchErr);
        }

        // 2. Play audible sound chime
        playTicketChime();

        // 3. Show native desktop notification
        const first = newTickets[0];
        const title = newTickets.length === 1 
          ? `🔔 تذكرة جديدة: #${first.displayId}` 
          : `🔔 وصلت ${newTickets.length} تذاكر جديدة!`;
        const body = `${first.customerName} — ${first.subject}`;
        showDesktopNotification(title, {
          body,
          onClick: () => {
            navigate('/tickets');
          },
        });

        // 4. Show floating toast banner only if not currently on /tickets page
        if (location.pathname !== '/tickets') {
          setActiveToast({
            id: `toast_${Date.now()}`,
            tickets: newTickets,
          });
          setTimeout(() => {
            setActiveToast((curr) => (curr?.id === `toast_${Date.now()}` ? null : curr));
          }, 8000);
        }
      }
    } catch (err) {
      console.warn('[GlobalTicketNotifier] Ticket check error:', err);
    }
  }, [isAuthenticated, navigate, location.pathname]);

  // Polling loop every 15 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check immediately on login/mount
    checkForNewTickets();

    const intervalId = setInterval(checkForNewTickets, 15000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, checkForNewTickets]);

  if (!activeToast || activeToast.tickets.length === 0) {
    return null;
  }

  const count = activeToast.tickets.length;
  const firstTicket = activeToast.tickets[0];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: isRTL ? '24px' : 'auto',
        left: isRTL ? 'auto' : '24px',
        zIndex: 99999,
        width: '380px',
        maxWidth: 'calc(100vw - 32px)',
        background: isLight ? '#ffffff' : 'linear-gradient(135deg, #0d1f3c 0%, #102a54 100%)',
        border: isLight ? '1px solid #3b82f6' : '1px solid rgba(59,130,246,0.6)',
        borderRadius: '14px',
        boxShadow: isLight 
          ? '0 10px 25px -5px rgba(59,130,246,0.3), 0 8px 10px -6px rgba(0,0,0,0.1)'
          : '0 20px 35px -5px rgba(0,0,0,0.6), 0 0 25px rgba(59,130,246,0.3)',
        padding: '16px',
        direction: isRTL ? 'rtl' : 'ltr',
        color: isLight ? '#0f172a' : '#f8fafc',
        animation: 'slideUpToast 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <style>{`
        @keyframes slideUpToast {
          from { transform: translateY(80px) scale(0.92); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes pulseBell {
          0%, 100% { transform: rotate(0deg) scale(1); }
          20% { transform: rotate(15deg) scale(1.1); }
          40% { transform: rotate(-15deg) scale(1.1); }
          60% { transform: rotate(10deg) scale(1.1); }
          80% { transform: rotate(-5deg) scale(1.1); }
        }
      `}</style>

      {/* Header Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              boxShadow: '0 2px 8px rgba(59,130,246,0.4)',
              animation: 'pulseBell 1.5s ease infinite',
            }}
          >
            <Ticket size={18} />
          </div>
          <div>
            <div style={{ 
              fontWeight: 800, 
              fontSize: '14px', 
              color: isLight ? '#1e40af' : '#60a5fa' 
            }}>
              {count === 1 ? '🔔 تذكرة دعم فني جديدة!' : `🔔 ${count} تذاكر جديدة!`}
            </div>
            <div style={{ fontSize: '11px', color: isLight ? '#64748b' : '#94a3b8' }}>
              تم استلامها للتو من بوابة الدعم
            </div>
          </div>
        </div>

        <button
          onClick={() => setActiveToast(null)}
          style={{
            background: 'none',
            border: 'none',
            color: isLight ? '#94a3b8' : '#64748b',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '6px',
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Ticket Details */}
      <div
        style={{
          background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)',
          borderRadius: '10px',
          padding: '10px 12px',
          marginBottom: '12px',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: '#3b82f6' }}>
            #{firstTicket.displayId}
          </span>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '20px',
              background: 'rgba(59,130,246,0.15)',
              color: '#3b82f6',
            }}
          >
            {firstTicket.priority}
          </span>
        </div>
        <div style={{ fontSize: '12px', fontWeight: 600, color: isLight ? '#1e293b' : '#e2e8f0', marginBottom: '2px' }}>
          {firstTicket.customerName}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: isLight ? '#64748b' : '#94a3b8',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {firstTicket.subject}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => {
            setActiveToast(null);
            navigate('/tickets');
          }}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '12px',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
          }}
        >
          <span>عرض التذاكر الآن</span>
          <ArrowLeft size={14} />
        </button>

        <button
          onClick={() => setActiveToast(null)}
          style={{
            background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
            border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '8px 14px',
            color: isLight ? '#475569' : '#94a3b8',
            fontWeight: 600,
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          إغلاق
        </button>
      </div>
    </div>
  );
};
